import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  ScrollView,
  Switch,
  ActivityIndicator,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useFocusEffect } from 'expo-router';
import { settingsApi, Settings } from '../../../src/api/client';
import { showAlert } from '../../../src/utils/alert';
import { requireConnectivity } from '../../../src/utils/connectivity';
import { useTheme } from '../../../src/theme/ThemeContext';
import { useThemedStyles } from '../../../src/theme/useThemedStyles';
import type { Theme } from '../../../src/theme/types';
import ScreenHeader from '../../../src/components/ScreenHeader';

const NOTIFICATION_PREFS = [
  { key: 'security', label: 'Security Alerts', description: 'New device logins, passkey changes' },
  { key: 'team', label: 'Team Updates', description: 'Invitations, member changes' },
  { key: 'goals', label: 'Goal & Activity Reminders', description: 'Deadlines, streaks, completions' },
  { key: 'system', label: 'System Announcements', description: 'Updates, maintenance notices' },
] as const;

const createStyles = (theme: Theme) => ({
  container: { flex: 1, backgroundColor: theme.colors.bgSecondary },
  content: { padding: 16, paddingBottom: 40 },
  card: {
    backgroundColor: theme.colors.background,
    borderRadius: 12,
    overflow: 'hidden' as const,
    ...theme.shadows.sm,
  },
  switchRow: {
    flexDirection: 'row' as const,
    justifyContent: 'space-between' as const,
    alignItems: 'center' as const,
    padding: 16,
  },
  switchLabelContainer: {
    flex: 1,
    marginRight: 12,
  },
  settingLabel: {
    fontSize: 15,
    fontWeight: '600' as const,
    color: theme.colors.textPrimary,
  },
  settingDescription: {
    fontSize: 13,
    color: theme.colors.textDisabled,
    marginTop: 2,
    lineHeight: 18,
  },
  divider: {
    height: 1,
    backgroundColor: theme.colors.bgTertiary,
    marginHorizontal: 16,
  },
  sectionGap: { height: 16 },
  disabledLabel: { color: theme.colors.textDisabled },
  centered: {
    flex: 1,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    backgroundColor: theme.colors.bgSecondary,
  },
});

export default function NotificationPreferencesScreen() {
  const router = useRouter();
  const styles = useThemedStyles(createStyles);
  const { colors } = useTheme();
  const [settings, setSettings] = useState<Settings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useFocusEffect(
    useCallback(() => {
      let cancelled = false;
      (async () => {
        try {
          const res = await settingsApi.get();
          if (!cancelled) setSettings(res.data);
        } catch {
          if (!cancelled) showAlert('Error', 'Failed to load notification preferences.');
        } finally {
          if (!cancelled) setIsLoading(false);
        }
      })();
      return () => { cancelled = true; };
    }, [])
  );

  const notificationsEnabled = settings?.notifications_enabled ?? false;

  const handleMasterToggle = async (value: boolean) => {
    if (!settings || isSaving) return;
    if (!requireConnectivity()) return;

    const prev = settings.notifications_enabled;
    setSettings({ ...settings, notifications_enabled: value });
    setIsSaving(true);

    try {
      const response = await settingsApi.update({ notifications_enabled: value });
      setSettings(response.data);
    } catch {
      setSettings({ ...settings, notifications_enabled: prev });
      showAlert('Error', 'Failed to update notification setting.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggle = async (prefKey: string, value: boolean) => {
    if (!settings || isSaving) return;
    if (!requireConnectivity()) return;

    const prevPrefs = settings.notification_preferences ?? {};
    setSettings({
      ...settings,
      notification_preferences: { ...prevPrefs, [prefKey]: value },
    });
    setIsSaving(true);

    try {
      const response = await settingsApi.update({
        notification_preferences: { [prefKey]: value },
      });
      setSettings(response.data);
    } catch {
      setSettings({ ...settings, notification_preferences: prevPrefs });
      showAlert('Error', 'Failed to update notification preference.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ScreenHeader
        title="Notifications"
        left={{
          label: 'Go back',
          icon: 'arrow-back',
          onPress: () => router.back(),
          testID: 'notification-preferences.back-button',
        }}
      />
      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.card}>
          <View style={styles.switchRow}>
            <View style={styles.switchLabelContainer}>
              <Text style={styles.settingLabel}>Enable Notifications</Text>
              <Text style={styles.settingDescription}>Receive in-app notifications and updates</Text>
            </View>
            <Switch
              value={notificationsEnabled}
              onValueChange={handleMasterToggle}
              trackColor={{ false: colors.border, true: colors.primaryLight }}
              thumbColor={notificationsEnabled ? colors.primary : colors.bgSecondary}
              accessibilityLabel="Enable Notifications"
              testID="notification-preferences.toggle-master"
            />
          </View>
        </View>

        <View style={styles.sectionGap} />

        <View style={[styles.card, !notificationsEnabled && { opacity: 0.5 }]}>
          {NOTIFICATION_PREFS.map((pref, index) => {
            const prefs = settings?.notification_preferences ?? {};
            const isEnabled = prefs[pref.key] !== false;
            return (
              <React.Fragment key={pref.key}>
                {index > 0 && <View style={styles.divider} />}
                <View style={styles.switchRow}>
                  <View style={styles.switchLabelContainer}>
                    <Text style={[styles.settingLabel, !notificationsEnabled && styles.disabledLabel]}>{pref.label}</Text>
                    <Text style={styles.settingDescription}>{pref.description}</Text>
                  </View>
                  <Switch
                    value={isEnabled}
                    onValueChange={(value) => handleToggle(pref.key, value)}
                    disabled={!notificationsEnabled}
                    trackColor={{ false: colors.border, true: colors.primaryLight }}
                    thumbColor={isEnabled ? colors.primary : colors.bgSecondary}
                    accessibilityLabel={pref.label}
                    testID={`notification-preferences.toggle-${pref.key}`}
                  />
                </View>
              </React.Fragment>
            );
          })}
        </View>
      </ScrollView>
    </View>
  );
}
